import { lazy, Suspense, useCallback } from "react";
import type { FeedArticleProjectionData } from "@/data-access/shared/fragments/feed-article-projection-fragments";
import useDiscoveryContentManagementStore from "@/store/discovery/discovery-content-management/discovery-content-management.slice";
import { useDiscoveryContext } from "@/store/discovery/discovery-params.context";

const AddAnArticleModal = lazy(
  () => import("@/shared/modals/article-modals/AddAnArticleModal"),
);

const PreviewAddedArticleModal = lazy(
  () => import("@/shared/modals/article-modals/PreviewAddedArticleModal"),
);

const BroadcastClipsExcludeModal = lazy(() =>
  import("./ExcludeDiscoveryItemModal").then((m) => ({
    default: m.BroadcastClipsExcludeModal,
  })),
);

const PodcastEpisodesExcludeModal = lazy(() =>
  import("./ExcludeDiscoveryItemModal").then((m) => ({
    default: m.PodcastEpisodesExcludeModal,
  })),
);

/**
 * Renders discovery modals (lazy-loaded). Open/close state is in the discovery content management store.
 */
const DiscoveryViewModals = () => {
  const { feedId } = useDiscoveryContext();
  const isAddArticleModalOpen = useDiscoveryContentManagementStore(
    (state) => state.isAddArticleModalOpen,
  );
  const previewArticle = useDiscoveryContentManagementStore(
    (state) => state.previewArticle,
  );
  const resetAddArticleModalState = useDiscoveryContentManagementStore(
    (state) => state.resetAddArticleModalState,
  );
  const setPreviewArticle = useDiscoveryContentManagementStore(
    (state) => state.setPreviewArticle,
  );
  const setIsAddArticleModalOpen = useDiscoveryContentManagementStore(
    (state) => state.setIsAddArticleModalOpen,
  );
  const setIsAddingArticle = useDiscoveryContentManagementStore(
    (state) => state.setIsAddingArticle,
  );

  const handleAddSuccessWithArticle = useCallback(
    (article: FeedArticleProjectionData) => {
      setIsAddArticleModalOpen(false);
      setPreviewArticle(article);
    },
    [],
  );

  const handleAddAnother = () => {
    setPreviewArticle(null);
    setIsAddArticleModalOpen(true);
  };

  return (
    <Suspense fallback={null}>
      <AddAnArticleModal
        open={isAddArticleModalOpen}
        onClose={resetAddArticleModalState}
        feedId={feedId}
        onSuccess={handleAddSuccessWithArticle}
        onAddingChange={setIsAddingArticle}
      />
      <PreviewAddedArticleModal
        open={previewArticle !== null}
        onClose={resetAddArticleModalState}
        onAddAnother={handleAddAnother}
        article={previewArticle}
      />
      <BroadcastClipsExcludeModal />
      <PodcastEpisodesExcludeModal />
    </Suspense>
  );
};

export default DiscoveryViewModals;
